/**
 * @fileoverview ClassificationBanner — fixed-position Overall Marking
 * banner per the Astro (Rocket Communications) spec for government UIs.
 *
 * Rules encoded:
 *  - Bold, centered, ALL CAPS text per Astro 4.20.6
 *  - Full-width span (mirrors physical document markings) per Astro 4.20.2
 *  - Fixed in the shell grid (top + bottom rows) so the banner cannot
 *    scroll out of view per Astro 4.20.5
 *  - Suffix may extend the marking with caveats following the standard
 *    structure CLASSIFICATION//SAFEGUARDING//DISTRIBUTION
 *    (e.g. "//FEDCON" or "//SAR-PROGRAM NICKNAME")
 *  - Color tokens are Astro's hex/text-color pairs (see ./levels.ts).
 *    Background colors are NOT required by the government but match
 *    common workstation/SF-label colors for at-a-glance recognition.
 *
 * Position prop: `"top"` (default) places the banner in the shell's
 * `classification-top` grid row; `"bottom"` places it in
 * `classification-bottom`. Both rows are always rendered; the bottom one
 * is best practice per Astro guidance.
 */

import { CLASSIFICATION_LEVELS, type ClassificationLevel } from "./levels";

interface ClassificationBannerProps {
  level: ClassificationLevel;
  /** Optional caveat suffix appended after the base banner text — e.g.
   * `"//FEDCON"`. Rendered verbatim (already all-caps per spec). */
  caveats?: string;
  /** Optional disclaimer rendered after a muted em-dash — signals a
   * demo/non-prod banner (e.g. "DEMONSTRATION ENVIRONMENT · SYNTHETIC DATA").
   * A real em-dash (not `//`) keeps it from reading as a CUI marking caveat. */
  trailer?: string;
  position?: "top" | "bottom";
  className?: string;
}

export function ClassificationBanner({
  level,
  caveats,
  trailer,
  position = "top",
  className,
}: ClassificationBannerProps) {
  const def = CLASSIFICATION_LEVELS[level];
  const text = caveats ? `${def.bannerText}${caveats}` : def.bannerText;

  return (
    <div data-testid="classification-banner"
      role="status"
      aria-label={trailer ? `Classification: ${text} — ${trailer}` : `Classification: ${text}`}
      className={[
        "bp-classification-banner",
        `bp-classification-banner-${position}`,
        `bp-classification-${def.key}`,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ backgroundColor: def.background, color: def.foreground }}
    >
      <span className="bp-classification-banner-text">{text}</span>
      {trailer ? (
        <>
          <span className="bp-classification-banner-dash" aria-hidden="true">—</span>
          <span className="bp-classification-banner-trailer">{trailer}</span>
        </>
      ) : null}
    </div>
  );
}
